import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from '@/react-intl';
import BaseApi from '@/uptk/core/api/BaseApi';
import { Button } from '@/design-system/ds-0/components/Button';
import { Modal } from '@/design-system/ds-0/components/Modal';
import { useEntityPermissions } from '@/uptk/core/hooks/useEntityPermissions';
import { invalidateStudioState } from '../data/studioMutationFeedback';

/**
 * MEM-STUDIO3 — geste Maker du journal des commandes : mesure à blanc (aucune écriture), puis « Compacter le journal » sur
 * confirmation explicite (empreinte du journal mesuré, motif, auteur). Jamais automatique ; le résultat est journalisé par le
 * serveur (événement « compact-journal ») et visible dans le fil. Même commande que le rail et le Copilote.
 */
export const COMPACT_JOURNAL_COMMAND = 'compact-journal';
export const JOURNAL_REASON_MAX = 500;

export interface JournalMeasure {
  dossier: string;
  events: number;
  bytes: number;
  copies: number;
  copyBytes: number;
  bytesAfter: number;
  savedBytes: number;
  digest: string;
  author?: string;
  items: { index: number; key: string; at: string; bytes: number; bytesAfter: number }[];
}

export interface JournalCompactionResult {
  compacted: number;
  eventsBefore: number;
  bytesBefore: number;
  bytesAfter: number;
  savedBytes: number;
  digestBefore: string;
  reason: string;
}

const base = (dossierId: string) => '/api/v1/module-studio/dossiers/' + encodeURIComponent(dossierId) + '/commands';

/** Mesure à blanc : lecture seule, aucune écriture côté serveur. */
export async function measureJournal(dossierId: string): Promise<JournalMeasure> {
  const response = await BaseApi.get(base(dossierId) + '/journal');
  return response.data as JournalMeasure;
}

/** Compactage confirmé : même requestId pour une nouvelle tentative du même geste (le serveur rend le même résultat). */
export async function compactJournal(dossierId: string, digest: string, reason: string, requestId: string): Promise<JournalCompactionResult> {
  const response = await BaseApi.post(base(dossierId) + '/' + COMPACT_JOURNAL_COMMAND, {
    confirmed: true, requestId, parameters: { digest, reason },
  });
  invalidateStudioState();
  return response.data as JournalCompactionResult;
}

/** Code de refus du serveur : `{code}` des erreurs du Studio ou `details[0]` d'une ResponseStatusException. */
export function journalRefusal(cause: unknown): string | null {
  const data = (cause as { response?: { data?: { code?: unknown; details?: unknown } } } | null)?.response?.data;
  if (data && typeof data.code === 'string') return data.code;
  if (data && Array.isArray(data.details) && typeof data.details[0] === 'string') return data.details[0];
  return null;
}

const refusalMessage = (code: string | null) => code === 'studio.command.journal_changed' ? 'moduleStudio.journal.changed'
  : code === 'studio.command.reason_required' ? 'moduleStudio.journal.reasonRequired' : 'moduleStudio.journal.failed';

const newRequestId = () => 'journal-' + crypto.randomUUID().replace(/-/g, '');

/** Tailles en mégaoctets, au format de la langue. */
export function useJournalSize() {
  const intl = useIntl();
  return (bytes: number) => intl.formatMessage({ id: 'moduleStudio.journal.megabytes' },
    { value: intl.formatNumber(bytes / 1_048_576, { maximumFractionDigits: 1 }) });
}

/** Résumé d'une mesure (partagé par le panneau du fil et le rail des commandes). */
export function JournalMeasureView({ measure }: { measure: JournalMeasure }) {
  const intl = useIntl();
  const size = useJournalSize();
  return <div role="status" className="flex flex-col gap-1 text-sm">
    <p className="m-0">{intl.formatMessage({ id: 'moduleStudio.journal.summary' }, { events: measure.events, size: size(measure.bytes), copies: measure.copies })}</p>
    <p className="m-0">{measure.copies > 0
      ? intl.formatMessage({ id: 'moduleStudio.journal.gain' }, { saved: size(measure.savedBytes), after: size(measure.bytesAfter) })
      : intl.formatMessage({ id: 'moduleStudio.journal.noCopy' })}</p>
    <p className="m-0 text-portal-ink-muted">{intl.formatMessage({ id: 'moduleStudio.journal.dryRun' })}</p>
  </div>;
}

/** Empreinte, auteur et motif : ce que la confirmation engage (partagé par le panneau et le rail). */
export function JournalConfirmationFields({ measure, reason, onReason, disabled }: { measure: JournalMeasure; reason: string; onReason: (value: string) => void; disabled?: boolean }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  return <div className="flex flex-col gap-2 text-sm">
    <p className="m-0">{t('moduleStudio.journal.confirmBody')}</p>
    <p className="m-0">{t('moduleStudio.journal.digest')} <code dir="ltr" className="break-all">{measure.digest}</code></p>
    <p className="m-0">{t('moduleStudio.journal.author')} <bdi>{measure.author ?? t('moduleStudio.state.unknownActor')}</bdi></p>
    <label className="flex flex-col gap-1">{t('moduleStudio.journal.reason')}
      <textarea className="min-h-11 w-full min-w-0 rounded border border-portal-border bg-portal-surface p-2 text-portal-ink" rows={3}
        maxLength={JOURNAL_REASON_MAX} required value={reason} disabled={disabled} onChange={event => onReason(event.target.value)} />
    </label>
    <p className="m-0 text-portal-ink-muted">{t('moduleStudio.journal.reasonHint')}</p>
  </div>;
}

/** Panneau du fil du dossier : « Mesurer le journal », puis « Compacter le journal » confirmé. */
export default function JournalCompaction({ dossierId }: { dossierId: string }) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const size = useJournalSize();
  const rights = useEntityPermissions('DESIGN_DOSSIER');
  const [measure, setMeasure] = useState<JournalMeasure | null>(null);
  const [measuring, setMeasuring] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<JournalCompactionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef('');
  const scope = useRef(0);
  useEffect(() => {
    scope.current += 1;
    setMeasure(null); setConfirming(false); setReason(''); setResult(null); setError(null); setBusy(false); setMeasuring(false);
  }, [dossierId]);
  const measureNow = useCallback(async () => {
    const token = ++scope.current;
    setMeasuring(true); setError(null); setResult(null);
    try { const value = await measureJournal(dossierId); if (scope.current === token) setMeasure(value); }
    catch { if (scope.current === token) setError('moduleStudio.journal.measureFailed'); }
    finally { if (scope.current === token) setMeasuring(false); }
  }, [dossierId]);
  const openConfirmation = () => { requestId.current = newRequestId(); setReason(''); setError(null); setConfirming(true); };
  const confirm = async () => {
    if (!measure || busy) return;
    const motif = reason.trim();
    if (!motif) { setError('moduleStudio.journal.reasonRequired'); return; }
    const token = scope.current;
    setBusy(true); setError(null);
    try {
      const done = await compactJournal(dossierId, measure.digest, motif, requestId.current);
      if (scope.current !== token) return;
      setResult(done); setConfirming(false); setMeasure(null);
    } catch (cause) {
      if (scope.current !== token) return;
      const code = journalRefusal(cause);
      setError(refusalMessage(code));
      // Journal changé depuis la mesure : la confirmation ne vaut plus ; une nouvelle mesure est exigée.
      if (code === 'studio.command.journal_changed') { setConfirming(false); setMeasure(null); }
    } finally { if (scope.current === token) setBusy(false); }
  };
  return <section aria-label={t('moduleStudio.journal.title')} className="flex flex-col gap-2 text-sm">
    <div className="flex flex-wrap items-center gap-2">
      <strong>{t('moduleStudio.journal.title')}</strong>
      <Button variant="secondary" disabled={measuring || busy} onClick={() => void measureNow()}>{t(measuring ? 'moduleStudio.journal.measuring' : 'moduleStudio.journal.measure')}</Button>
      {measure && measure.copies > 0 ? <Button disabled={busy || !rights.can('UPDATE')} onClick={openConfirmation}>{t('moduleStudio.command.compact-journal')}</Button> : null}
    </div>
    {measure ? <JournalMeasureView measure={measure} /> : null}
    {result ? <p role="status" className="m-0">{t('moduleStudio.journal.done', { copies: result.compacted, before: size(result.bytesBefore), after: size(result.bytesAfter) })}</p> : null}
    {error && !confirming ? <p role="alert" className="m-0">{t(error)}</p> : null}
    <Modal open={confirming && !!measure} onClose={() => { if (!busy) setConfirming(false); }} title={t('moduleStudio.journal.confirmTitle')}
      closeLabel={t('common.action.cancel')} variant="confirm"
      primaryAction={{ label: t('moduleStudio.journal.confirm'), loading: busy, disabled: !reason.trim(), onClick: () => void confirm() }}
      secondaryAction={{ label: t('common.action.cancel'), onClick: () => setConfirming(false) }}>
      {measure ? <JournalMeasureView measure={measure} /> : null}
      {measure ? <JournalConfirmationFields measure={measure} reason={reason} onReason={setReason} disabled={busy} /> : null}
      {error ? <p role="alert">{t(error)}</p> : null}
    </Modal>
  </section>;
}
